import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  MessageSquare,
  RefreshCw,
  QrCode,
  KeyRound,
  LogOut,
  RotateCw,
  Loader2,
  Info,
  Plus,
  Trash2,
  CheckCircle2,
  Webhook,
} from "lucide-react";

type ConnState = "open" | "close" | "connecting" | "unknown";

interface InvokeResult {
  ok: boolean;
  status: number;
  data: any;
  instance: string;
  active?: string;
  target?: string;
}

function extractAdminError(result: InvokeResult) {
  const data = result.data;
  const upstreamMessage =
    data?.response?.message ||
    data?.message ||
    data?.error ||
    (typeof data === "string" ? data : "");
  const target = result.target || data?.target;
  const status = result.status ? `HTTP ${result.status}` : "Erro";
  return [status, upstreamMessage, target ? `Endpoint: ${target}` : ""].filter(Boolean).join(" — ");
}

interface InstanceListItem {
  name: string;
  connectionStatus?: string;
  profileName?: string;
  ownerJid?: string;
  purpose?: "platform" | "restaurant";
  restaurantId?: string | null;
  phoneNumber?: string | null;
}

async function invokeAdmin(action: string, body: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("evolution-admin", {
    body: { action, ...body },
  });
  if (error) throw error;
  const result = data as InvokeResult;
  if (!result.ok) throw new Error(extractAdminError(result));
  return result;
}

function stateLabel(s: ConnState) {
  if (s === "open") return { label: "Conectado", variant: "default" as const, color: "bg-emerald-500" };
  if (s === "connecting")
    return { label: "Conectando…", variant: "secondary" as const, color: "bg-amber-500" };
  if (s === "close") return { label: "Desconectado", variant: "destructive" as const, color: "bg-red-500" };
  return { label: "Desconhecido", variant: "outline" as const, color: "bg-muted" };
}

function normalizeInstances(raw: any): InstanceListItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((it: any) => {
      const i = it?.instance ?? it;
      const managed = it?.managed ?? {};
      return {
        name: i?.name ?? i?.instanceName ?? "",
        connectionStatus: i?.connectionStatus ?? i?.state ?? i?.status,
        profileName: i?.profileName,
        ownerJid: i?.ownerJid ?? i?.owner,
        purpose: managed.purpose ?? "platform",
        restaurantId: managed.restaurant_id ?? null,
        phoneNumber: managed.phone_number ?? null,
      };
    })
    .filter((i) => i.name);
}

export default function AdminWhatsAppPage() {
  const [state, setState] = useState<ConnState>("unknown");
  const [activeInstance, setActiveInstance] = useState<string>("");
  const [instances, setInstances] = useState<InstanceListItem[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string>("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newInstanceName, setNewInstanceName] = useState("");
  const [newInstanceRestaurantId, setNewInstanceRestaurantId] = useState<string>("__platform__");
  const [restaurants, setRestaurants] = useState<Array<{ id: string; name: string }>>([]);
  const [profile, setProfile] = useState<{ owner?: string; profileName?: string; profilePicUrl?: string }>({});
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [qrBase64, setQrBase64] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingNumber, setPairingNumber] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await invokeAdmin("listInstances");
      const list = normalizeInstances(res.data);
      setInstances(list);
      const active = res.active || "";
      setActiveInstance(active);
      setSelectedInstance((cur) => cur || active);
    } catch (e: any) {
      toast.error(`Falha ao listar instâncias: ${e.message || e}`);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!activeInstance) return;
    setLoadingStatus(true);
    try {
      const res = await invokeAdmin("connectionState");
      const s = (res.data?.instance?.state as ConnState) ?? "unknown";
      setState(s);

      try {
        const info = await invokeAdmin("fetchInstance");
        const arr = Array.isArray(info.data) ? info.data : [];
        const inst = arr[0]?.instance ?? arr[0] ?? {};
        setProfile({
          owner: inst.ownerJid || inst.owner,
          profileName: inst.profileName,
          profilePicUrl: inst.profilePicUrl,
        });
      } catch {
        // ignore
      }

      if (s === "open" && qrBase64) {
        setQrBase64(null);
        setPairingCode(null);
        toast.success("WhatsApp conectado!");
      }
    } catch (e: any) {
      toast.error(`Falha ao consultar status: ${e.message || e}`);
    } finally {
      setLoadingStatus(false);
    }
  }, [qrBase64, activeInstance]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("restaurants")
        .select("id, name")
        .order("name");
      setRestaurants((data ?? []) as Array<{ id: string; name: string }>);
    })();
  }, []);

  const activeIsManaged = !!activeInstance &&
    instances.some((i) => i.name === activeInstance);

  useEffect(() => {
    if (!activeInstance || !activeIsManaged) return;
    refreshStatus();
    const id = window.setInterval(refreshStatus, 5000);
    return () => window.clearInterval(id);
  }, [refreshStatus, activeInstance, activeIsManaged]);

  const handleConnect = async (withNumber = false) => {
    setBusy(withNumber ? "pairing" : "qr");
    try {
      const res = await invokeAdmin("connect", withNumber ? { number: pairingNumber } : {});
      const d = res.data || {};
      const b64: string | undefined = d.base64 || d?.qrcode?.base64;
      const pc: string | undefined = d.pairingCode || d?.qrcode?.pairingCode;
      if (b64) {
        const normalized = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
        setQrBase64(normalized);
      } else {
        setQrBase64(null);
      }
      setPairingCode(pc || null);
      if (!b64 && !pc) toast.error("Resposta sem QR/código. Tente reiniciar a instância.");

      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(refreshStatus, 3000);
      window.setTimeout(() => {
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 120000);
    } catch (e: any) {
      toast.error(`Falha ao gerar QR: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleLogout = async () => {
    setBusy("logout");
    try {
      await invokeAdmin("logout");
      toast.success("Instância desconectada");
      setQrBase64(null);
      setPairingCode(null);
      await refreshStatus();
      await refreshList();
    } catch (e: any) {
      toast.error(`Falha no logout: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleRestart = async () => {
    setBusy("restart");
    try {
      await invokeAdmin("restart");
      toast.success("Instância reiniciada");
      setQrBase64(null);
      setPairingCode(null);
      setTimeout(refreshStatus, 1500);
    } catch (e: any) {
      toast.error(`Falha ao reiniciar: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleSetActive = async () => {
    if (!selectedInstance || selectedInstance === activeInstance) return;
    setBusy("setActive");
    try {
      await invokeAdmin("setActiveInstance", { instanceName: selectedInstance });
      toast.success(`Instância ativa: ${selectedInstance}`);
      setQrBase64(null);
      setPairingCode(null);
      setProfile({});
      await refreshList();
    } catch (e: any) {
      toast.error(`Falha ao definir instância ativa: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleCreate = async () => {
    const name = newInstanceName.trim();
    if (!/^[a-z0-9][a-z0-9-_]{1,48}$/i.test(name)) {
      toast.error("Nome inválido. Use letras, números, - ou _ (2-49 caracteres).");
      return;
    }
    setBusy("create");
    try {
      const restaurantId = newInstanceRestaurantId === "__platform__" ? null : newInstanceRestaurantId;
      const res = await invokeAdmin("createInstance", {
        instanceName: name,
        restaurantId,
      });
      const d = res.data || {};
      const b64: string | undefined = d.base64 || d?.qrcode?.base64;
      const pc: string | undefined = d.pairingCode || d?.qrcode?.pairingCode;
      if (b64) {
        const normalized = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
        setQrBase64(normalized);
      }
      if (pc) setPairingCode(pc);
      toast.success(
        restaurantId
          ? `Instância "${name}" criada e vinculada ao restaurante`
          : `Instância "${name}" criada e definida como ativa`,
      );
      setCreateOpen(false);
      setNewInstanceName("");
      setNewInstanceRestaurantId("__platform__");
      await refreshList();
    } catch (e: any) {
      toast.error(`Falha ao criar instância: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (name: string) => {
    setBusy(`delete:${name}`);
    try {
      await invokeAdmin("deleteInstance", { instanceName: name });
      toast.success(`Instância "${name}" excluída`);
      await refreshList();
    } catch (e: any) {
      toast.error(`Falha ao excluir: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const handleApplyWebhook = async (name: string) => {
    setBusy(`webhook:${name}`);
    try {
      await invokeAdmin("applyWebhook", { instance: name });
      toast.success(`Webhook reaplicado em "${name}"`);
    } catch (e: any) {
      toast.error(`Falha ao aplicar webhook: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const sl = stateLabel(state);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">WhatsApp da Plataforma</h1>
          <p className="text-muted-foreground">
            Gerencie a instância única usada para enviar notificações a todos os restaurantes.
          </p>
        </div>
      </div>

      {/* Instance Management */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Instâncias Evolution</CardTitle>
              <CardDescription>
                Crie, selecione e exclua instâncias. A "ativa" é usada para enviar todas as notificações.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={refreshList} disabled={loadingList}>
                {loadingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Atualizar</span>
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                <span className="ml-2">Criar nova</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              O webhook deste projeto é aplicado <strong>por instância</strong>{" "}
              (não no global do servidor Evolution). Instâncias de outros
              sistemas que compartilham a mesma VPS continuam intactas. Ao
              criar uma nova instância ou clicar em <em>Conectar</em> o webhook
              é registrado automaticamente; use o botão{" "}
              <Webhook className="inline h-3 w-3" /> para reaplicar manualmente.
            </AlertDescription>
          </Alert>
          {activeInstance && !activeIsManaged && !loadingList && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription>
                A instância ativa <code>{activeInstance}</code> não foi criada
                por este painel. Crie uma nova abaixo e ela será definida
                automaticamente como ativa. Nenhuma instância existente no
                servidor Evolution será tocada.
              </AlertDescription>
            </Alert>
          )}
          {instances.length === 0 && !loadingList && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Nenhuma instância encontrada no servidor. Clique em <strong>Criar nova</strong> para começar.
              </AlertDescription>
            </Alert>
          )}

          {instances.length > 0 && (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[240px] space-y-2">
                  <Label>Instância ativa</Label>
                  <Select value={selectedInstance} onValueChange={setSelectedInstance}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione…" />
                    </SelectTrigger>
                    <SelectContent>
                      {instances.map((i) => (
                        <SelectItem key={i.name} value={i.name}>
                          {i.name}
                          {i.connectionStatus ? ` — ${i.connectionStatus}` : ""}
                          {i.name === activeInstance ? "  ★" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSetActive}
                  disabled={busy === "setActive" || !selectedInstance || selectedInstance === activeInstance}
                >
                  {busy === "setActive" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  <span className="ml-2">Definir como ativa</span>
                </Button>
              </div>

              <div className="rounded-lg border divide-y">
                {instances.map((i) => (
                  <div key={i.name} className="flex items-center justify-between p-3 gap-3">
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        <code className="text-sm">{i.name}</code>
                        {i.name === activeInstance && (
                          <Badge variant="default" className="text-xs">Ativa</Badge>
                        )}
                        {i.purpose === "restaurant" ? (
                          <Badge variant="secondary" className="text-xs">
                            {restaurants.find((r) => r.id === i.restaurantId)?.name ?? "Restaurante"}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Plataforma</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {i.connectionStatus || "—"}
                        {i.profileName ? ` · ${i.profileName}` : ""}
                        {i.ownerJid ? ` · ${i.ownerJid}` : ""}
                        {i.phoneNumber ? ` · ${i.phoneNumber}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleApplyWebhook(i.name)}
                        disabled={busy === `webhook:${i.name}`}
                        title="Reaplicar webhook deste projeto nesta instância"
                      >
                        {busy === `webhook:${i.name}` ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Webhook className="h-4 w-4" />
                        )}
                      </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={i.name === activeInstance || busy === `delete:${i.name}`}
                          title={i.name === activeInstance ? "Defina outra instância como ativa antes de excluir" : "Excluir"}
                        >
                          {busy === `delete:${i.name}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir instância "{i.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            A sessão WhatsApp será encerrada e a instância removida do servidor. Não há como desfazer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(i.name)}>Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar nova instância</DialogTitle>
            <DialogDescription>
              Escolha um nome único (ex.: <code>altus-prod</code>). Após criar, escaneie o QR Code para conectar o WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-name">Nome da instância</Label>
            <Input
              id="new-name"
              value={newInstanceName}
              placeholder="altus-prod"
              onChange={(e) => setNewInstanceName(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ""))}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Letras minúsculas, números, traços ou sublinhados. A instância será definida automaticamente como ativa.
            </p>
            <div className="pt-2 space-y-2">
              <Label>Vincular a um restaurante</Label>
              <Select value={newInstanceRestaurantId} onValueChange={setNewInstanceRestaurantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__platform__">
                    Plataforma (avisos internos)
                  </SelectItem>
                  {restaurants.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Plataforma = instância única usada pela equipe Lovable para alertas. Restaurante = usada para
                falar com clientes daquele restaurante (auto-reply do cardápio + notificações de pedido).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={busy === "create" || newInstanceName.length < 2}>
              {busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span className="ml-2">Criar</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Status da Conexão</CardTitle>
              <CardDescription>Atualizado automaticamente a cada 5 segundos</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={refreshStatus} disabled={loadingStatus || !activeInstance}>
              {loadingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Atualizar</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`h-3 w-3 rounded-full ${sl.color}`} />
            <Badge variant={sl.variant} className="text-sm">
              {sl.label}
            </Badge>
            <span className="text-sm text-muted-foreground">
              Instância: <code className="bg-muted px-1.5 py-0.5 rounded">{activeInstance || "—"}</code>
            </span>
          </div>

          {(profile.profileName || profile.owner) && (
            <div className="mt-4 flex items-center gap-3 rounded-lg border p-3">
              {profile.profilePicUrl && (
                <img src={profile.profilePicUrl} alt="" className="h-10 w-10 rounded-full" />
              )}
              <div className="text-sm">
                <div className="font-medium">{profile.profileName || "—"}</div>
                <div className="text-muted-foreground">{profile.owner || ""}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconnect */}
      {activeInstance && activeIsManaged && state !== "open" && (
        <Card>
          <CardHeader>
            <CardTitle>Reconectar WhatsApp</CardTitle>
            <CardDescription>
              Escaneie o QR Code no WhatsApp do telefone (Aparelhos conectados) ou use um código de pareamento.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => handleConnect(false)} disabled={busy === "qr"}>
                {busy === "qr" ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                <span className="ml-2">Gerar QR Code</span>
              </Button>
            </div>

            {qrBase64 && (
              <div className="flex flex-col items-center gap-3 rounded-lg border p-4 bg-muted/30">
                <img src={qrBase64} alt="QR Code WhatsApp" className="h-64 w-64" />
                <p className="text-sm text-muted-foreground text-center">
                  WhatsApp → Configurações → Aparelhos conectados → Conectar um aparelho
                </p>
              </div>
            )}

            <div className="space-y-2 rounded-lg border p-4">
              <Label htmlFor="pairing-number">Telefone para código de pareamento</Label>
              <div className="flex gap-2">
                <Input
                  id="pairing-number"
                  placeholder="5516997096672"
                  value={pairingNumber}
                  onChange={(e) => setPairingNumber(e.target.value.replace(/\D/g, ""))}
                  className="font-mono"
                />
                <Button
                  variant="secondary"
                  onClick={() => handleConnect(true)}
                  disabled={busy === "pairing" || pairingNumber.length < 10}
                >
                  {busy === "pairing" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4" />
                  )}
                  <span className="ml-2">Gerar código</span>
                </Button>
              </div>
              {pairingCode && (
                <div className="mt-3 text-center">
                  <p className="text-sm text-muted-foreground">Digite no WhatsApp:</p>
                  <p className="text-4xl font-bold tracking-[0.4em] font-mono mt-1">{pairingCode}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {activeInstance && activeIsManaged && (
        <Card>
          <CardHeader>
            <CardTitle>Ações</CardTitle>
            <CardDescription>Use com cuidado — afetam o WhatsApp de toda a plataforma.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={busy === "restart"}>
                  <RotateCw className="h-4 w-4" />
                  <span className="ml-2">Reiniciar instância</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reiniciar instância?</AlertDialogTitle>
                  <AlertDialogDescription>
                    A conexão será derrubada e reaberta. Pode ser preciso escanear o QR novamente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestart}>Reiniciar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={busy === "logout"}>
                  <LogOut className="h-4 w-4" />
                  <span className="ml-2">Desconectar (logout)</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Desconectar WhatsApp?</AlertDialogTitle>
                  <AlertDialogDescription>
                    A sessão será encerrada e nenhuma notificação será enviada até reconectar via QR.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLogout}>Desconectar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          URL e API key do servidor são lidas dos secrets (<code>EVOLUTION_API_URL</code>,{" "}
          <code>EVOLUTION_API_KEY</code>). O nome da instância ativa é gerenciado por esta tela e
          armazenado em <code>platform_settings</code>.
        </AlertDescription>
      </Alert>
    </div>
  );
}
